import React from "react";
import {
  FaWhatsapp,
  FaPhoneVolume,
  FaClock,
} from "react-icons/fa";
import { FiMail } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { indiaSupport } from "../../content/indiaSiteContent";

type Action = {
  title: string;
  description: string;
  icon: React.ReactNode;
  badge?: string;
  badgeType?: "success" | "warning";
  footer: React.ReactNode;
  href: string;
  highlight?: boolean;
};

const actions: Action[] = [
  {
    title: "WhatsApp Support",
    description:
      "The fastest way to ask questions, share details, and get trip updates.",
    icon: <FaWhatsapp className="text-3xl" />,
    badge: "24/7 Available",
    badgeType: "success",
    footer: indiaSupport.whatsappDisplay,
    href: indiaSupport.whatsappHref,
  },
  {
    title: "Call Support",
    description:
      "Speak directly with our team for holiday planning and visa assistance.",
    icon: <FaPhoneVolume className="text-3xl" />,
    badge: "Open Line",
    badgeType: "success",
    footer: indiaSupport.phoneDisplay,
    href: indiaSupport.phoneHref,
    highlight: true,
  },
  {
    title: "Email Us",
    description:
      "Best for detailed requirements, documentation questions, and follow-ups.",
    icon: <FiMail className="text-3xl" />,
    badge: "Inbox Support",
    badgeType: "warning",
    footer: indiaSupport.email,
    href: indiaSupport.emailHref,
  },
];

const QuickActions: React.FC = () => {
  const { t } = useTranslation();

  return (
    <section className="relative z-20 -mt-10 py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {actions.map((action, index) => (
            <a
              key={action.title}
              href={action.href}
              className={`insp-card group relative flex flex-col items-center overflow-hidden p-8 text-center ${
                action.highlight ? "border border-brand-pink/20" : ""
              }`}
            >
              {action.highlight && (
                <div className="absolute right-0 top-0 rounded-bl-lg bg-brand-pink px-3 py-1 text-xs font-bold text-white">
                  {t("24/7 Line")}
                </div>
              )}

              <div
                className={`mb-6 flex h-16 w-16 items-center justify-center rounded-full transition-transform duration-300 group-hover:scale-110 ${
                  index === 0
                    ? "bg-green-50 text-green-500"
                    : index === 1
                      ? "bg-pink-50 text-brand-pink"
                      : "bg-blue-50 text-brand-blue"
                }`}
              >
                {action.icon}
              </div>

              <h3 className="mb-2 text-xl font-bold text-brand-dark">
                {t(action.title)}
              </h3>

              <p className="mb-4 text-sm text-gray-500">{t(action.description)}</p>

              {action.badge && (
                <div
                  className={`mb-4 flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
                    action.badgeType === "success"
                      ? "bg-green-50 text-green-600"
                      : "bg-yellow-50 text-yellow-600"
                  }`}
                >
                  {action.badgeType === "success" ? (
                    <span className="h-2 w-2 animate-pulse rounded-full bg-green-500" />
                  ) : (
                    <FaClock />
                  )}
                  {t(action.badge)}
                </div>
              )}

              <span className="text-sm font-medium text-brand-pink group-hover:underline">
                {typeof action.footer === "string" ? t(action.footer) : action.footer}
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default QuickActions;
